import React from "react";
import classNames from "classnames";
import dynamic from "next/dynamic";
import Head from "next/head";
import Script from "next/script";

import { Article, ArticleExcerpt, MenuItems } from "custom-types/Wordpress";
import { useEventTracker } from "hooks/useEventTracker";

import ArticlesCarousel from "components/ArticlesCarousel";
import NewsArticleFooter from "components/news/NewsArticleFooter";
import NewsArticleHeader from "components/news/NewsArticleHeader";
import NewsEmailSignup from "components/news/NewsEmailSignup";
import NewsSocialShareBar from "components/news/NewsSocialShareBar";
import NewsTags from "components/news/NewsTags";

import NewsAd from "../Ads/NewsAd";
import NewsAdBillboard from "../Ads/NewsAdBillboard";
import NewsAdLower from "../Ads/NewsAdLower";
import ArticleContent from "../ArticleContent";
import FeaturedArticleHeader from "../NewsArticleHeader/FeaturedArticleHeader/FeaturedArticleHeader";
import NewsRequiredContainer from "../NewsRequiredContainer";
import NewsSideBar from "../NewsSideBar";

const NewsDispensaryCarousel = dynamic(
  () => import("../NewsDispensaryCarousel")
);

type ArticleProps = {
  article: Article;
  relatedArticles: ArticleExcerpt[];
  menuItems: MenuItems;
};

const ArticleLayout: React.FC<ArticleProps> = ({
  article,
  menuItems,
  relatedArticles,
}) => {
  const {
    author,
    categories,
    disableAds,
    id,
    manualAdPlacement,
    primaryCategorySlug,
    regionRestriction,
    schemaJson,
    scripts,
    seoHeadContent,
    slug,
    styles,
    tags,
    title,
  } = article;
  const { publishEvent } = useEventTracker();

  const primaryCategory = categories?.length ? categories[0] : null;

  const displayDispensaryCarousel = !article.brand.name;

  return (
    <>
      <Head>
        <link
          rel="alternate"
          type="application/rss+xml"
          title={`Leafly » ${article.title} Feed`}
          href={`${article.link}/feed`}
        />
      </Head>

      <NewsRequiredContainer
        styles={styles}
        scripts={scripts}
        id={id}
        regionRestriction={regionRestriction}
        seoHeadContent={seoHeadContent}
        primaryCategorySlug={primaryCategorySlug}
        title={title}
        categories={categories}
        authorName={author.name}
        schemaJson={schemaJson}
        tags={tags}
        slug={slug}
      >
        {!disableAds ? <NewsAdBillboard /> : <></>}
        <div className="relative">
          {article.isFeatured && <FeaturedArticleHeader article={article} />}
          <div className="container my-xxl">
            <div className="row">
              <div
                className={classNames("col md:col-3", {
                  "absolute md:relative top-[20px] w-[calc(100%-20px)] md:w-auto":
                    article.isFeatured,
                })}
              >
                <NewsSideBar menuItems={menuItems} disableAds={disableAds} />
              </div>
              <div className="col md:col-9">
                {!article.isFeatured && <NewsArticleHeader article={article} />}

                <ArticleContent item={article} className="md:mt-xl" />

                {displayDispensaryCarousel && <NewsDispensaryCarousel />}
                {!disableAds && !manualAdPlacement && <NewsAdLower />}
                <div className="flex flex-row justify-between flex-wrap mt-xxl">
                  <div className="md:max-w-2/3">
                    <NewsTags tags={article.tags} />
                  </div>
                  <div className="md:max-w-1/3 mt-md md:mt-none">
                    <NewsSocialShareBar
                      link={article.link}
                      title={article.title}
                    />
                  </div>
                </div>
                <div id="article-footer" className="w-full my-xxl">
                  <NewsArticleFooter article={article} />
                </div>
                <ArticlesCarousel
                  articleCardOnClick={(index, articleTitle) =>
                    publishEvent({
                      action: "click",
                      category: "news",
                      label: `Related articles slot #${
                        index + 1
                      }: ${articleTitle}`,
                    })
                  }
                  articles={relatedArticles}
                  className="my-xxl"
                  title={`The latest in ${primaryCategory || "news"}`}
                  seeMoreHref={
                    primaryCategory ? `/news/${primaryCategorySlug}` : "/news"
                  }
                  seeMoreOnClick={() =>
                    publishEvent({
                      action: "click",
                      category: "news",
                      label: `show all`,
                    })
                  }
                />
                <NewsEmailSignup source="article-footer" />
                <div id="disqus_thread"></div>
                <Script
                  id="disqus-script"
                  strategy="lazyOnload"
                  dangerouslySetInnerHTML={{
                    __html: `
                    const disqus_config = () => {
                      this.page.url = "${article.link}";  
                      // Due to previous integration Disqus on wordpress end, this 
                      // is unfortnately the way the identifier need
                      this.page.identifier = "${article.id} https://www.leafly.com/wp-home/?p=${article.id}"; 
                    };
                    (() => {
                      var d = document, s = d.createElement('script');
                      s.src = 'https://leaflyevents.disqus.com/embed.js';
                      s.setAttribute('data-timestamp', +new Date());
                      (d.head || d.body).appendChild(s);
                    })();
                  `,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </NewsRequiredContainer>
      {!disableAds ? (
        <NewsAd
          unit="/outofpage"
          sizes={{ desktop: [1, 1], mobile: [1, 1] }}
          className="sticky"
        />
      ) : (
        <></>
      )}
    </>
  );
};

export default ArticleLayout;
